import {useEffect, useState} from 'react';

import styled from 'styled-components';

import {attachModal} from '~/shared/components/ReduxModal';
import {PieCloseModalButton, PieModalHeader} from '~/shared/components/Modals/styled';
import PieButton from '~/shared/theme/pie/PieButton';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import {body14Normal, body16Normal, headingS, headingXS} from '~/shared/theme/typography';
import {flexColumn} from '~/shared/theme/FlexLayout';
import {media} from '~/shared/theme/utils';
import {ModalName as ClosedRestaurantsModalNames} from '~/shared/types/closedRestaurants';
import type {PieGeneralModalArgs} from '~/shared/types/closedRestaurants';
import {createLogger} from '~/shared/logging';

export type {ButtonConfig, PieGeneralModalArgs} from '~/shared/types/closedRestaurants';

const logger = createLogger('PieGeneralModal');

type Props = {
  closeModal: () => void;
  args: PieGeneralModalArgs;
};

const Root = styled.div`
  ${flexColumn}
  align-items: center;
  justify-content: center;
  padding: 0 16px 32px;
  width: 100%;
  height: auto;

  &&& {
    background-color: ${({theme}) => theme.colors.surface};
  }

  ${media.minLargeMobile`
    padding: 0 32px 24px;
    &&& {
      border-radius: 20px;
      overflow: hidden;
      width: 500px;
    }
  `}
`;

const StyledImage = styled(ImageWithAlt)`
  width: 150px;
  height: 150px;
  margin-top: 8px;
  ${media.minLargeMobile`
    width: 200px;
    height: 200px;
    margin-top: 24px;
  `}
`;

const TextWrapper = styled.div`
  ${flexColumn}
  align-items: center;
  text-align: center;
  gap: 8px;
  margin-top: 24px;
  width: 100%;
  color: ${({theme}) => theme.colors.charcoal80};
`;

const Header = styled.h2`
  ${headingXS}
  ${media.minLargeMobile`
    ${headingS}
  `}
`;

const SubHeader = styled.p`
  ${body14Normal}
  line-height: 20px;
  ${media.minLargeMobile`
    ${body16Normal}
    line-height: 24px;
  `}
`;

const ButtonsContainer = styled.div`
  ${flexColumn}
  align-items: center;
  width: 100%;
  gap: 16px;
  margin-top: 32px;
  ${media.minLargeMobile`
    flex-direction: row;
  `}
`;

const ActionButton = styled(PieButton)`
  width: 100%;
  ${media.minLargeMobile`
    flex: 1;
    padding: 12px 16px;
  `}
`;

const PieGeneralModal = ({closeModal, args}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const {imgSrc, imgAlt, header, subHeader, primaryButton, secondaryButton, onMount, onClose} = args;

  useEffect(() => {
    onMount?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const primaryButtonClickHandler = async () => {
    setIsLoading(true);
    try {
      if (primaryButton.onClick) {
        await primaryButton.onClick();
      }
    } catch (error) {
      logger.warn('Uncaught error in primaryButton click handler');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Root data-test-id="pieGeneralModal">
      <PieModalHeader hideOnMobile />
      <PieCloseModalButton
        onClick={() => {
          onClose?.();
          closeModal();
          }}
        hideOnMobile
      />
      {imgAlt !== undefined ? (
        <StyledImage src={imgSrc} alt={imgAlt} />
      ) : (
        <StyledImage src={imgSrc} noAlt />
      )}
      <TextWrapper>
        <Header id="modal-title" data-test-id="pieGeneralModal_header">{header}</Header>
        <SubHeader data-test-id="pieGeneralModal_subHeader" dangerouslySetInnerHTML={{__html: subHeader}} />
      </TextWrapper>
      <ButtonsContainer>
        <ActionButton
          variant="primaryLarge"
          data-test-id="pieGeneralModal_primaryButton"
          onClick={primaryButtonClickHandler}
          isLoading={isLoading}
        >
          {primaryButton.label}
        </ActionButton>
        {secondaryButton && (
          <ActionButton variant="secondaryLarge" data-test-id="pieGeneralModal_secondaryButton" onClick={secondaryButton.onClick}>
            {secondaryButton.label}
          </ActionButton>
        )}
      </ButtonsContainer>
    </Root>
  );
};

PieGeneralModal.mobileBottomSheet = true;
PieGeneralModal.hideCloseButton = true;

[...Object.values(ClosedRestaurantsModalNames), 'reIssueOrReplaceMoneycardStatus', 'moneycardFeedbackModal'].forEach(name => attachModal(name, PieGeneralModal));

export default PieGeneralModal;
